/**
 * F-131 — a point's steps, derived from its committed structure (Tim, 8 Sep 2026).
 *
 * No steps table. A point's steps are its linked lessons and assets (any strength: doing them
 * earns the first three levels, a lesson weighing 1.5 to an asset's 1, and Documented needs
 * every step — Tim, 10 Sep 2026) and its primary diagnostic question, which completes
 * Implemented and Optimised once the point is Documented — or sets every level on a point with
 * nothing to do (Tim, 9 Sep 2026). Done-state comes from the server's result (`points[id].done`,
 * the satisfied evidence as "kind:ref"). Lessons and assets are links; the question is the
 * answer control (owner/admin) or the current answer as text (member).
 */
import type { DerivedStep } from './types';
import type { ResultPoint, RoadmapPointPayload } from '@/api/roadmap';
import type { RoadmapKpi } from '@/lib/prime-score';

/** What the KPI steps need from the view: the visible Results rows and whether figures are withheld. */
export interface KpiStepContext {
  rowsByCode: Map<string, RoadmapKpi>;
  /** The viewer has no finance access, so a measured row's absence means "withheld", not "unknown". */
  figuresWithheld: boolean;
}

export const IN_PLACE_LEVEL = 3;

const LESSON_STRENGTHS = new Set(['teaches', 'related']);
const ASSET_STRENGTHS = new Set(['is_the_point', 'related']);

function satisfied(point: ResultPoint | undefined, kind: string, ref: string): boolean {
  return point?.done.includes(`${kind}:${ref}`) ?? false;
}

/** The point's locked steps by step id (hermes migration 682). */
function lockedById(state: ResultPoint | undefined): Map<string, NonNullable<DerivedStep['locked']>> {
  return new Map((state?.locked ?? []).map((l) => [l.ref, { tier: l.tier, reason: l.reason }]));
}

/**
 * Does the point have lessons or assets to do that the organisation can open? The server's own
 * rule (hermes migration 682), mirrored for a result stored before `questionOpen` existed.
 */
export function hasSomethingToDo(point: RoadmapPointPayload, state?: ResultPoint): boolean {
  const locked = lockedById(state);
  return (
    (point.lessons ?? []).some((l) => LESSON_STRENGTHS.has(l.strength) && !locked.has(`lesson:${l.lesson_id}`)) ||
    (point.assets ?? []).some(
      (a) => ASSET_STRENGTHS.has(a.strength) && !locked.has(`asset:${a.evidence_asset_id}`),
    )
  );
}

export function deriveSteps(
  point: RoadmapPointPayload,
  state: ResultPoint | undefined,
  kpis?: KpiStepContext,
): DerivedStep[] {
  const steps: DerivedStep[] = [];
  const locked = lockedById(state);

  const lessons = (point.lessons ?? []).filter((l) => LESSON_STRENGTHS.has(l.strength));
  for (const link of lessons) {
    const courseId = link.lesson?.course?.id ?? null;
    const kind = link.strength === 'teaches' ? 'lesson_teaches' : 'lesson_related';
    steps.push({
      id: `lesson:${link.lesson_id}`,
      kind: 'lesson',
      ref: link.lesson_id,
      title: link.lesson?.name ?? 'Lesson',
      done: satisfied(state, kind, link.lesson_id),
      ...(locked.has(`lesson:${link.lesson_id}`) ? { locked: locked.get(`lesson:${link.lesson_id}`) } : {}),
      ...(courseId
        ? {
          href: {
            to: '/learn/courses/$courseId/lessons/$lessonId',
            params: { courseId, lessonId: link.lesson_id },
          },
        }
        : {}),
    });
  }

  for (const link of (point.assets ?? []).filter((a) => ASSET_STRENGTHS.has(a.strength))) {
    const kind = link.strength === 'is_the_point' ? 'asset_is_the_point' : 'asset_related';
    steps.push({
      id: `asset:${link.evidence_asset_id}`,
      kind: 'asset',
      ref: link.evidence_asset_id,
      title: link.asset?.name ?? 'Asset',
      done: satisfied(state, kind, link.evidence_asset_id),
      ...(locked.has(`asset:${link.evidence_asset_id}`)
        ? { locked: locked.get(`asset:${link.evidence_asset_id}`) }
        : {}),
      href: { to: '/assets/$assetId', params: { assetId: link.evidence_asset_id } },
    });
  }

  const primary = (point.questions ?? []).find((q) => q.is_primary);
  if (primary) {
    // A result stored before this build carries no `questionOpen`: read it the server's way.
    const open = state?.questionOpen ?? (state?.built || !hasSomethingToDo(point, state));
    const answer = state?.answer ?? null;
    steps.push({
      id: `question:${primary.question_code}`,
      kind: 'question',
      ref: primary.question_code,
      title: primary.question?.question_text ?? `Diagnostic question ${primary.question_code}`,
      done: open && answer !== null,
      question: {
        code: primary.question_code,
        type: primary.question?.type ?? 'unknown',
        options: primary.question?.options ?? [],
        open,
        answer,
        sharedWith: state?.sharedWith ?? 0,
      },
    });
  }

  // The KPIs that measure the point (roadmap_point_kpis), the primary first: a fresh in-target
  // figure is Implemented, with the question's top answer Optimised (Tim, 9 Sep 2026).
  for (const link of state?.kpis ?? []) {
    const row = kpis?.rowsByCode.get(link.code) ?? null;
    const withheld = row === null && (kpis?.figuresWithheld ?? false);
    if (row === null && !withheld) continue; // not in this result at all (nothing seeded)
    steps.push({
      id: `kpi:${link.code}`,
      kind: 'kpi',
      ref: link.code,
      title: row?.name ?? 'KPI',
      done: row?.counted === true && row.status === 'green',
      ...(link.primary ? {} : { detail: 'Also measured by' }),
      kpi: { code: link.code, primary: link.primary, row, withheld },
    });
  }

  return steps;
}

/**
 * Every lesson and every asset step is satisfied.
 *
 * A related lesson used to be labelled "Related — optional" on the card and left out of this
 * count, on the reading that the lessons which teach a point are its course and a related one is
 * extra. The 10 Sep ladder does not work that way: `stepLevel` weighs every linked lesson at one
 * and a half, every asset at one, and a point only reaches Documented when every linked step is
 * done. So the label told a builder a step was optional while the engine was holding the point
 * back for it.
 *
 * Worked through: a point with a teaching lesson, a related lesson and an asset. Finish the
 * teaching lesson and the asset and the server is at (1.5 + 1) / (1.5 + 1.5 + 1) × 3 = 2.25,
 * which floors to informal, not Documented, and the question stays closed because it is gated on
 * `built`. The card said the remaining step was optional, so nothing explained the wall.
 *
 * If an optional tier is wanted later it has to exist in the derivation first, not only here.
 */
export function allDoingStepsDone(steps: DerivedStep[]): boolean {
  // A locked step (migration 682) is shown, never counted.
  const doing = steps.filter((s) => s.kind !== 'question' && s.kind !== 'kpi' && !s.locked);
  return doing.length > 0 && doing.every((s) => s.done);
}
